import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import moment from 'moment';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  EndorsementServiceFeaturesGetApplicationGroupByUidAttachment,
  UpsertApplicationGroupRequest,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { sortListByAttribute } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import {
  DataCard,
  FileUpload,
  Loader,
  SdirCheckbox,
  SdirDatePicker,
  SdirRadioButton,
  Select,
} from '@sdir/sds';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import { routes } from '../Routes';
import StatusCard, { Status } from '../Molecules/StatusCard';
import FileName from '../Atoms/FileName';
import { courseInstitutionApi, endorsementApi } from '../../httpclient';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import { toBase64 } from '../../helpers/fileHelpers';
import { InputError } from '../Atoms/InputError';
import { AttachmentType, RequirementType } from '../../constants/enums';
import { formatBackendValidationErrors } from '../../helpers/validationHelpers';

const RequirementsPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { application: currentApplication, setApplication } = useCurrentApplication();
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const [courseFile, setCourseFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const [grossTonnage, setGrossTonnage] = useState<string>('');
  const [nisDate, setNisDate] = useState<Date | null>(null);
  const [nisInstitution, setNisInstitution] = useState<string | null>(
    currentApplication?.result?.nisCourseIssuedInstitution || null
  );
  const [confirmDeclaration, setConfirmDeclaration] = useState<boolean>(false);
  const [checkedDispensation, setCheckedDispensation] = useState<boolean>(false);

  const { showNIS, showDeclaration, showDispensation, showGrossTonnage } = useMemo(() => {
    const items = currentApplication?.result?.requirements || [];
    return {
      showNIS: items?.some((i) => i?.requirementName === RequirementType.NisCourse),
      showDeclaration: items?.some(
        (i) => i?.requirementName === RequirementType.DeclarationOfCompetence
      ),
      showDispensation: items?.some((i) => i?.requirementName === RequirementType.Dispensation),
      showGrossTonnage: items?.some(
        (i) => i?.requirementName === RequirementType.GrossTonnageLessThan250
      ),
    };
  }, [currentApplication?.result, grossTonnage]);

  const { existingCourse, declarationOfCompetence, dispensation, grossTonnageLessThan250 } =
    useMemo(() => {
      const application = currentApplication?.result;
      const course: EndorsementServiceFeaturesGetApplicationGroupByUidAttachment | undefined =
        application?.attachments?.find((a) => a.attachmentType === AttachmentType.NisCourse);
      return {
        existingCourse: course || null,
        declarationOfCompetence: application?.declarationOfCompetence,
        dispensation: application?.dispensation,
        grossTonnageLessThan250: application?.grossTonnageLessThan250,
      };
    }, [currentApplication?.result]);

  const {
    errorObject: submitError,
    loading: submitLoading,
    callApi: submitStep,
  } = useApi({
    apiFunction: (payload) => endorsementApi.upsertApplicationGroup(payload),
    callApiOnInit: false,
    successCallback: (result) => {
      if (result && setApplication) {
        setApplication({ result });
      }
      const path = generatePath(routes.summary, { applicationUid: result.uid });
      navigate(path);
    },
  });

  const {
    result: institutions,
    errorObject: institutionsError,
    loading: institutionsLoading,
  } = useApi({
    apiFunction: () =>
      courseInstitutionApi.v1InstitutioncourseGetinstitutionsbycoursecodeCourseCodeGet('REG'),
    callApiOnInit: true,
  });

  const institutionOptions = useMemo(() => {
    const items = institutions || [];
    const none = [{ name: 'Other', value: 'none' }];
    let result = items.map((i) => ({ name: i.name || '', value: i.organizationNumber || '' }));
    result = sortListByAttribute(result, 'name');
    return none.concat(result);
  }, [institutions]);

  useEffect(() => {
    if (submitError && submitError.response?.status === 422) {
      const errors = submitError?.response?.data?.validationErrors || [];
      const backendErrors = formatBackendValidationErrors(errors);
      setFieldErrors(backendErrors);
    }
  }, [submitError]);

  useEffect(() => {
    const { nisCourseIssuedDate, nisCourseIssuedInstitution } = currentApplication?.result || {};
    if (nisCourseIssuedDate) {
      setNisDate(moment(nisCourseIssuedDate.substring(0, 10)).toDate());
    }
    if (nisCourseIssuedInstitution) {
      setNisInstitution(nisCourseIssuedInstitution);
    }
  }, [
    currentApplication?.result?.nisCourseIssuedDate,
    currentApplication?.result?.nisCourseIssuedInstitution,
  ]);

  const dispensationAndTonnageStatus = useMemo(() => {
    if (grossTonnage === '0-250') return Status.Valid;
    if (grossTonnage === '250+' && checkedDispensation) return Status.Valid;
    return Status.Warning;
  }, [grossTonnage, checkedDispensation]);

  const validateFields = useCallback(() => {
    const application = currentApplication?.result;
    const nationality = application?.person?.nationality;
    const errors: any = {};
    if (showDeclaration && !confirmDeclaration) errors.declaration = requiredText;
    const courseAttachment = application?.attachments?.find(
      (a) => a.attachmentType === AttachmentType.NisCourse
    );
    if (showNIS && !courseFile && !courseAttachment) errors.course = requiredText;
    if (showNIS && courseFile?.size === 0) {
      errors.course = intl.formatMessage({ id: 'general.form.error.emptyfile' });
    }
    if (showGrossTonnage) {
      if (!grossTonnage) {
        errors.grossTonnage = requiredText;
      }
      if (grossTonnage === '250+' && !checkedDispensation) {
        errors.dispensation = intl.formatMessage({
          id: 'application.requirements.error.dispensation',
        });
      }
      if (grossTonnage === '250+' && checkedDispensation && nationality === 'RU') {
        errors.dispensation = intl.formatMessage({
          id: 'application.requirements.error.nationality',
        });
      }
    }
    return errors;
  }, [
    currentApplication?.result,
    confirmDeclaration,
    courseFile,
    requiredText,
    showDeclaration,
    showNIS,
    showGrossTonnage,
    showDispensation,
    checkedDispensation,
    grossTonnage,
  ]);

  const handleSubmit = useCallback(async () => {
    const application = currentApplication?.result;
    if (!application) return;

    const errors = validateFields();
    setFieldErrors(errors);
    if (Object.keys(errors)?.length > 0) return;

    let existingAttachments = application.attachments || [];

    if (showNIS) {
      const hasExistingCourse = !!existingAttachments?.find(
        (a) => a?.attachmentType === AttachmentType.NisCourse
      );
      if (courseFile) {
        if (hasExistingCourse) {
          existingAttachments = existingAttachments.filter(
            (a) => a.attachmentType !== AttachmentType.NisCourse
          );
        }
        const course64 = await toBase64(courseFile);
        existingAttachments.push({
          attachmentFileName: courseFile?.name,
          attachmentType: AttachmentType.NisCourse,
          attachmentFile: course64 as string,
        });
      }
    }
    const payload: UpsertApplicationGroupRequest | any = {
      ...application,
      attachments: existingAttachments,
      declarationOfCompetence: showDeclaration ? confirmDeclaration : false,
      grossTonnageLessThan250: showGrossTonnage ? grossTonnage === '0-250' : null,
      dispensation: showDispensation && grossTonnage === '250+' ? checkedDispensation : null,
      nisCourseIssuedDate: nisDate ? moment(nisDate).format('YYYY-MM-DD') : null,
      nisCourseIssuedInstitution:
        nisInstitution && nisInstitution !== 'none' ? nisInstitution : null,
    };
    submitStep(payload);
  }, [
    currentApplication?.result,
    confirmDeclaration,
    courseFile,
    nisDate,
    nisInstitution,
    showDeclaration,
    showNIS,
    validateFields,
  ]);

  const onChangeGrossTonnage = (event: any) => {
    const value = event?.target?.value;
    setGrossTonnage(value);
    setFieldErrors({});
  };

  useEffect(() => {
    if (declarationOfCompetence) {
      setConfirmDeclaration(true);
    }
  }, [declarationOfCompetence]);

  useEffect(() => {
    const requirements = currentApplication?.result?.requirements;
    if (requirements && requirements.length === 0) {
      // No requirements needed, continue to summary step
      const uid = currentApplication?.result?.uid;
      const path = generatePath(routes.summary, { applicationUid: uid });
      navigate(path);
    }
  }, []);

  useEffect(() => {
    if (grossTonnageLessThan250) {
      setGrossTonnage('0-250');
    }
    if (dispensation && !grossTonnageLessThan250) {
      setCheckedDispensation(true);
      setGrossTonnage('250+');
    }
  }, [dispensation, grossTonnageLessThan250]);

  if (currentApplication?.loading) {
    return <Loader />;
  }

  return (
    <ApplicationLayout
      canNavNext={!submitLoading}
      canNavBack={!submitLoading}
      heading={intl.formatMessage({ id: 'application.new.title' })}
      ingress={intl.formatMessage({ id: 'application.new.ingress' })}
      exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
      navBackLabel={intl.formatMessage({ id: 'general.back' })}
      navNextLabel={intl.formatMessage({ id: 'general.next' })}
      timeline={<ApplicationSteps />}
      onNavNextClick={handleSubmit}
    >
      <DataCard>
        <RequirementsInfo>
          <h2>{intl.formatMessage({ id: 'application.requirements.title' })}</h2>
          <ItemList>
            {showDeclaration && (
              <StatusCard
                status={confirmDeclaration ? Status.Valid : Status.Warning}
                title={intl.formatMessage({ id: 'application.requirements.declaration.title' })}
                description={intl.formatMessage({
                  id: 'application.requirements.declaration.description',
                })}
                actions={
                  <>
                    <SdirCheckbox
                      checked={confirmDeclaration}
                      title={intl.formatMessage({
                        id: 'application.requirements.declaration.checkbox',
                      })}
                      onClick={() => setConfirmDeclaration(!confirmDeclaration)}
                    />
                    {fieldErrors?.declaration && <InputError>{fieldErrors.declaration}</InputError>}
                  </>
                }
              />
            )}
            {showNIS && (
              <StatusCard
                status={courseFile || existingCourse ? Status.Valid : Status.Warning}
                title={intl.formatMessage({ id: 'application.requirements.course.title' })}
                description={intl.formatMessage({
                  id: 'application.requirements.course.description',
                })}
                actions={
                  <NisWrapper>
                    <FileUpload
                      allowedTypes=".png,.jpg,.jpeg,.pdf"
                      buttonLabel="Upload documentation"
                      onFilesSelected={(files) => {
                        setCourseFile(files?.[0]);
                        setFieldErrors({});
                      }}
                    />
                    <FileName
                      fileName={courseFile?.name || existingCourse?.attachmentFileName || ''}
                    />
                    {fieldErrors?.course && <InputError>{fieldErrors.course}</InputError>}
                    {fieldErrors?.attachmentFile && (
                      <InputError>{fieldErrors.attachmentFile}</InputError>
                    )}
                    <br />
                    <NisFieldRow>
                      <SdirDatePicker
                        label="Course date"
                        className="nis-date-input"
                        value={nisDate}
                        maxDate={new Date()}
                        onChange={(d: Date | null) => setNisDate(d)}
                        disabled={institutionsLoading}
                      />
                      <Select
                        defaultVal="Select institution"
                        listLabel="Select an option"
                        size="300px"
                        value={nisInstitution}
                        options={institutionOptions}
                        handleChange={(v) => setNisInstitution(v)}
                        disabled={institutionsLoading}
                      />
                    </NisFieldRow>
                  </NisWrapper>
                }
              />
            )}
            {showGrossTonnage && (
              <StatusCard
                status={dispensationAndTonnageStatus}
                title={intl.formatMessage({ id: 'application.requirements.grossTonnage.title' })}
                description={intl.formatMessage({
                  id: 'application.requirements.grossTonnage.description',
                })}
                actions={
                  <RadioContainer as="fieldset" id="radiogroup-grosstonnage">
                    <SdirRadioButton
                      value="250+"
                      checked={grossTonnage === '250+'}
                      title={intl.formatMessage({
                        id: 'application.requirements.grossTonnage.option2',
                      })}
                      onClick={(val) => onChangeGrossTonnage({ target: { value: val } })}
                      name="grossTonnage"
                    />
                    {showDispensation && (
                      <DispensationWrapper>
                        <DispensationDesc>
                          {intl.formatMessage({
                            id: 'application.requirements.dispensation.description',
                          })}
                        </DispensationDesc>
                        <SdirCheckbox
                          checked={grossTonnage === '0-250' ? false : checkedDispensation}
                          title={intl.formatMessage({
                            id: 'application.requirements.dispensation.checkbox',
                          })}
                          onClick={() => {
                            setCheckedDispensation(!checkedDispensation);
                            setFieldErrors({});
                          }}
                        />
                        {fieldErrors?.dispensation && (
                          <InputError>{fieldErrors.dispensation}</InputError>
                        )}
                      </DispensationWrapper>
                    )}
                    <SdirRadioButton
                      value="0-250"
                      checked={grossTonnage === '0-250'}
                      title={intl.formatMessage({
                        id: 'application.requirements.grossTonnage.option1',
                      })}
                      onClick={(val) => onChangeGrossTonnage({ target: { value: val } })}
                      name="grossTonnage"
                    />
                    {fieldErrors?.grossTonnage && (
                      <InputError>{fieldErrors.grossTonnage}</InputError>
                    )}
                  </RadioContainer>
                }
              />
            )}
          </ItemList>
        </RequirementsInfo>
      </DataCard>
    </ApplicationLayout>
  );
};

export default RequirementsPage;

const RequirementsInfo = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;
`;

const RadioContainer = styled.fieldset`
  border: none;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  & > * {
    margin-top: 20px;
  }
`;

const DispensationWrapper = styled.div`
  padding-left: 55px;
  margin-bottom: 10px;
`;

const DispensationDesc = styled.span`
  display: block;
  font-style: italic;
  font-size: 14px;
  line-height: 1.25;
  margin-bottom: 10px;
`;

const NisWrapper = styled.div``;

const NisFieldRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  .nis-date-input {
    max-width: 300px;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    flex-direction: column;
    .nis-date-input {
      margin-bottom: 10px;
    }
  }
`;
